// Functions that build, resolve and list variable references from data already in hand. This file
// doesn't import `nodes/registry`, so node types can import it without an import cycle.

import type React from 'react';
import type { VariableRef, VariableValue } from '@/proto/logiblocks/logiblocks';
import { VariableSource, VariableType, VariableDesc } from '@/proto/common';
import type { NodeOutput } from '@/proto/variable_schema';
import { store } from '@/redux-toolkit/store';
import { selectCouriersState } from '@/redux-toolkit/slice/onboardingSlice';
import type { FieldGroup, FieldItem } from '@/apis/logiblocksAPI';
import type { LBNodeInfo } from '../types';
import { NodeCategory } from '../types';
import {
  TRIGGER_ICON_COLOR,
  ACTION_ICON_COLOR,
  CONTROL_FLOW_ICON_COLOR,
  NOTIFICATIONS_ICON_COLOR,
} from '../styles';
import { getGenericExampleForType } from './variableTypeLabels';
import { getDeclaredOutputs, getEnumValues } from './variableDefinitions';
import {
  DerivedVariableDesc,
  getDerivedVariables,
  getPickableDerivedVariables,
} from './metaVariableCatalog';
import {
  SYSTEM_NODE_ID,
  SYSTEM_VARIABLE_CATALOG,
} from './systemVariableCatalog';
import { shouldHideField as checkFieldVisibility } from './fieldVisibilityRules';
import type { TriggerFilterContext } from './triggerFilterUtils';

const categoryIconColors: Record<string, string> = {
  [NodeCategory.TRIGGER]: TRIGGER_ICON_COLOR,
  [NodeCategory.ACTION]: ACTION_ICON_COLOR,
  [NodeCategory.CONTROL_FLOW]: CONTROL_FLOW_ICON_COLOR,
  [NodeCategory.NOTIFICATIONS]: NOTIFICATIONS_ICON_COLOR,
};

/**
 * Every real VariableType, i.e. every numeric enum value except UNRECOGNIZED.
 *
 * Passing this as getAvailableVariables' typeFilter means "don't restrict by
 * type" while still routing through the filtered branch of
 * getAncestorFieldDescriptors -- which, unlike the unfiltered branch, keeps a
 * meta-variable's own opaque ID (e.g. shipment_id) alongside its derived
 * fields. A free-text field that genuinely needs the raw ID (a webhook URL,
 * say) wants exactly that; a caller happy to only show derived, human-facing
 * fields (a Slack message body) should keep passing no filter at all.
 */
export const ALL_VARIABLE_TYPES: VariableType[] = Object.values(
  VariableType
).filter((v): v is VariableType => typeof v === 'number' && v >= 0);

/**
 * Builds a reference from a variable name written as text, where a derived name
 * follows a dot.
 *
 * The only place a name in that form becomes a reference. The workflow agent's
 * ops and template text write a name that way.
 *
 * TODO(tim): delete once the agent's ops carry the two names separately and
 * `templateToChunks` is deleted.
 */
export function parseVariableRefName(
  nodeId: string,
  variableName: string
): VariableRef {
  const dotIndex = variableName.indexOf('.');
  if (dotIndex <= 0) {
    return {
      node_id: nodeId,
      variable_name: variableName,
      derived_variable_name: '',
      source: VariableSource.SOURCE_UNSPECIFIED,
    };
  }
  return {
    node_id: nodeId,
    variable_name: variableName.slice(0, dotIndex),
    derived_variable_name: variableName.slice(dotIndex + 1),
    source: VariableSource.SOURCE_UNSPECIFIED,
  };
}

/**
 * The name of a variable reference without its node id, as the author reads it:
 * "temperature" for a plain variable, "shipment_id.status" for a derived one.
 */
export function variableRefName(ref: VariableRef): string {
  if (ref.derived_variable_name === '') {
    return ref.variable_name;
  }
  return `${ref.variable_name}.${ref.derived_variable_name}`;
}

/**
 * Whether a reference names a system variable, such as the current time.
 */
export function isSystemRef(ref: VariableRef): boolean {
  return ref.source === VariableSource.SOURCE_SYSTEM;
}

export interface AncestorNodeContext {
  /** The node ID */
  nodeId: string;
  /** The source node's LBNodeInfo data. */
  nodeInfo: LBNodeInfo;
  /** The node instance's display name — its custom name if renamed, else nodeInfo.label. */
  nodeDisplayName: string;
  /** Output variables filtered by scope visibility */
  filteredOutputVariables: NodeOutput[];
}

/**
 * Fills in the source of a reference built from a node id and a variable name
 * alone, which is how the workflow agent's ops name one. A reference that
 * already carries a source, or that names nothing the ancestors publish, is
 * returned as it is.
 *
 * TODO(tim): delete once the agent's ops carry the source themselves.
 */
export function resolveVariableRefSource(
  ref: VariableRef,
  ancestorVariables: AncestorNodeContext[]
): VariableRef {
  if (ref.source !== VariableSource.SOURCE_UNSPECIFIED) {
    return ref;
  }
  if (ref.node_id === SYSTEM_NODE_ID) {
    return { ...ref, source: VariableSource.SOURCE_SYSTEM };
  }
  const ancestor = ancestorVariables.find((av) => av.nodeId === ref.node_id);
  if (!ancestor) {
    return ref;
  }
  const isNamed = (o: NodeOutput): boolean =>
    o.variable!.name === ref.variable_name;
  // An op can reference a node output before a later op sets the setting
  // that publishes it, so the node type's declared outputs are checked too.
  // A declared output is SOURCE_NODE_TYPE whatever the settings.
  const output =
    ancestor.filteredOutputVariables.find(isNamed) ??
    getDeclaredOutputs(ancestor.nodeInfo.protobufNodeDataKey).find(isNamed);
  if (!output) {
    return ref;
  }
  return { ...ref, source: output.source };
}

/**
 * Fills in the source of a reference read from template text. When the node
 * publishes an author's variable and a node output of the same name, the
 * author's variable is the one the text meant, since that's the one the
 * backend renders for text.
 *
 * TODO(tim/gio): delete with `templateToChunks`, once no stored workflow holds
 * a template as text and the agent's ops carry chunks.
 */
export function resolveTemplateTextRefSource(
  ref: VariableRef,
  ancestorVariables: AncestorNodeContext[]
): VariableRef {
  const ancestor = ancestorVariables.find((av) => av.nodeId === ref.node_id);
  const authorVariable = ancestor?.filteredOutputVariables.find(
    (o) =>
      o.source === VariableSource.SOURCE_USER &&
      o.variable!.name === ref.variable_name
  );
  if (authorVariable) {
    return { ...ref, source: VariableSource.SOURCE_USER };
  }
  return resolveVariableRefSource(ref, ancestorVariables);
}

/**
 * Finds the picker item a stored reference names, among the groups
 * getAvailableVariablesGroup built. A reference matches only an item with its
 * source.
 */
export function findFieldItem(
  ref: VariableRef,
  groups: FieldGroup[]
): FieldItem | undefined {
  // A node's fields are spread over several groups: one per meta-variable and
  // one for its plain variables.
  const items = groups
    .filter((g) => g.node_id === ref.node_id)
    .flatMap((g) => g.items);
  return items.find(
    (item) =>
      item.field_name === ref.variable_name &&
      (item.derived_field_name ?? '') === ref.derived_variable_name &&
      item.source === ref.source
  );
}

/**
 * One variable an author can pick at a point in the workflow: a system variable,
 * an ancestor's plain output, or one derived field of an ancestor's meta-variable.
 * Carries what a picker row shows and what the chosen reference stores.
 */
export interface VariableListItem {
  /** ID of the node (or SYSTEM_NODE_ID) that produces this variable. */
  nodeId: string;
  /** Group header label shown in the picker (e.g. node label or "System"). */
  groupName: string;
  /** Optional icon for the group header. */
  getGroupIcon?: (size: number) => React.ReactNode;
  /** Variable name stored in the VariableRef: "shipment_id" for a meta field, "temperature" for a plain variable. */
  variableName: string;
  /** Derived variable name stored in the VariableRef: "status" for a meta field, "" for a plain variable. */
  derivedVariableName: string;
  /** Who declared the variable, stored in the VariableRef. */
  source: VariableSource;
  /** Friendly label shown in the picker list. Falls back to the variable's name if omitted. */
  displayName?: string;
  /** Example value shown in the variable picker. */
  exampleString?: string;
  /** Value type, shown as a type hint in the picker. */
  type: VariableType;
}

/** The reference a picked variable stores. */
export function variableListItemRef(item: VariableListItem): VariableRef {
  return {
    node_id: item.nodeId,
    variable_name: item.variableName,
    derived_variable_name: item.derivedVariableName,
    source: item.source,
  };
}

export function displayNameToSnakeCase(name: string): string {
  return name
    .toLowerCase()
    .replace(/-/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_');
}

/** Idempotent on names that are already display names ("Original SMS"). */
export function snakeCaseToDisplayName(name: string): string {
  return name
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * Label for a variable in the picker. A declared display name is shown as written; only a
 * variable without one falls back to its humanized name.
 */
function getVariableDisplayName(variable: VariableDesc): string {
  return variable.display_name || snakeCaseToDisplayName(variable.name);
}

/**
 * Example value for a variable in the picker. A variable that declares none borrows a
 * generic one for its type, and types with no generic show nothing.
 */
function getVariableExample(variable: VariableDesc): string | undefined {
  return variable.sample_value || getGenericExampleForType(variable.type);
}

type LiteralFormatter = (v: VariableValue) => string | undefined;

// Resolve a CompanyCourier UUID to "<title>" via Redux. The conditional editor
// saves a CompanyCourier.id (the per-company instance UUID); CourierItem.id is
// the global Courier.id, so we match on .company_courier instead. Returns
// undefined when the courier list hasn't loaded or the UUID is unknown.
function formatTransportProviderLiteral(v: VariableValue): string | undefined {
  if (v.string_value == null) {
    return undefined;
  }
  const couriers = selectCouriersState(store.getState()).courierList;
  const match = couriers.find((c) => c.company_courier === v.string_value);
  return match ? `"${match.title}"` : undefined;
}

// Type-driven formatters for literals whose wire string is a semantic UUID.
// New lookup types (LOCATION, PRODUCT, …) drop in here.
const SEMANTIC_LITERAL_FORMATTERS: Partial<
  Record<VariableType, LiteralFormatter>
> = {
  [VariableType.TRANSPORT_PROVIDER]: formatTransportProviderLiteral,
};

/**
 * Short display string for a literal VariableValue (e.g. `"FedEx"`, `42`,
 * `true`, `4/13/2026 3:00 PM`). A labeled id shows its name, and a list of
 * them shows each name (`"Cold chain ops", "QA"`). Semantic-UUID types
 * (TRANSPORT_PROVIDER, …) resolve through SEMANTIC_LITERAL_FORMATTERS;
 * everything else falls back to the wire-type representation.
 */
export function formatVariableValue(v: VariableValue): string {
  const semanticFormatter = SEMANTIC_LITERAL_FORMATTERS[v.type];
  if (semanticFormatter) {
    const semantic = semanticFormatter(v);
    if (semantic != null) {
      return semantic;
    }
  }
  if (v.string_value != null) {
    return `"${v.string_value}"`;
  }
  if (v.labeled_id_value != null) {
    return `"${v.labeled_id_value.name}"`;
  }
  if (v.labeled_id_list_value != null) {
    const quotedNames: string[] = [];
    for (const item of v.labeled_id_list_value.items) {
      quotedNames.push(`"${item.name}"`);
    }
    return quotedNames.join(', ');
  }
  if (v.number_value != null) {
    return String(v.number_value);
  }
  if (v.bool_value != null) {
    return v.bool_value ? 'true' : 'false';
  }
  if (v.datetime_value != null) {
    const d = new Date(v.datetime_value.unix_millis);
    const isMidnight =
      d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0;
    return isMidnight
      ? d.toLocaleDateString()
      : `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }
  return '?';
}

/**
 * Whether an output is the one a reference names: same name and same source.
 */
function outputMatchesRef(
  output: NodeOutput,
  ref: VariableRef,
  name: string
): boolean {
  return output.variable!.name === name && output.source === ref.source;
}

/**
 * Finds what a reference names: a published variable, or a derived variable
 * read off one.
 *
 * A published name can itself contain a dot ("est._delivery"), and is matched
 * whole.
 */
export function lookupVariableRef(
  ref: VariableRef,
  publishedVariables: NodeOutput[]
):
  | { kind: 'variable'; variable: VariableDesc }
  | { kind: 'derived'; derived: DerivedVariableDesc }
  | undefined {
  const output = publishedVariables.find((o) =>
    outputMatchesRef(o, ref, variableRefName(ref))
  );
  if (output) {
    return { kind: 'variable', variable: output.variable! };
  }
  const derived = resolveDerivedVariable(ref, publishedVariables);
  return derived ? { kind: 'derived', derived } : undefined;
}

/**
 * Resolves a reference to a derived variable (e.g. "shipment_id.status") to its
 * definition, or undefined if it isn't a valid reference.
 *
 * @param ref - A normalized variable reference. Undefined for a reference with no derived name.
 * @param publishedVariables - Variables published by the node that produced this variable
 */
export function resolveDerivedVariable(
  ref: VariableRef,
  publishedVariables: NodeOutput[]
): DerivedVariableDesc | undefined {
  if (ref.derived_variable_name === '') {
    return undefined;
  }
  const baseOutput = publishedVariables.find((o) =>
    outputMatchesRef(o, ref, ref.variable_name)
  );
  if (!baseOutput) {
    return undefined;
  }
  return getDerivedVariables(baseOutput.variable!.type)?.find(
    (f) => f.fieldName === ref.derived_variable_name
  );
}

/**
 * One selectable field published by an ancestor node. Meta-variables are
 * already expanded into their derived fields (one descriptor each), so callers
 * never re-expand.
 */
interface AncestorFieldDescriptor {
  /** Node that publishes this variable; saved into the VariableRef. */
  nodeId: string;
  /** Publishing node's name, shown as the picker's group header. */
  groupName: string;
  /** Draws the publishing node's icon next to its group header. */
  getGroupIcon?: (size: number) => React.ReactNode;
  /** Publishing node's category, which picks the group's color. */
  category: NodeCategory;
  /** Variable name saved into the VariableRef: "shipment_id" for a meta field, "temperature" for a plain variable. */
  variableName: string;
  /** Derived variable name saved into the VariableRef: "status" for a meta field, unset for a plain variable. */
  derivedVariableName?: string;
  /** Who declared the variable, saved into the VariableRef. */
  source: VariableSource;
  /** Label shown in the picker row. */
  displayName?: string;
  /** Sample value shown beside the picker row. */
  exampleString?: string;
  /** Value type, which decides the comparison operators the editor offers. */
  type: VariableType;
  /** Field has a fixed set of allowed values. */
  isEnum: boolean;
  /** Field can hold more than one value. */
  isMultiSelect: boolean;
  /** Field's allowed values are presented in labelled sub-groups. */
  group?: boolean;
  /**
   * Set when this field belongs to a meta-variable: `baseName` ("shipment_id")
   * keys its group; `displayName` ("Shipment") labels it. The grouped picker
   * gives each meta-variable its own group. Undefined for a plain variable.
   */
  meta?: { baseName: string; displayName: string };
}

/**
 * Lists every field a node can reference from its ancestors, expanding each
 * meta-variable into its derived fields. Shared source of truth for both the
 * flat and grouped variable pickers.
 *
 * When typeFilter is given, only fields of a matching type are returned; a
 * meta-variable matched directly also yields its own opaque-ID field. When a
 * trigger context is given, fields the trigger makes irrelevant are dropped (a
 * meta-variable's own ID field is always kept). System variables are not
 * included; each caller prepends them in its own shape. Fields the company is
 * not entitled to pick (getPickableDerivedVariables) are left out unless
 * includeGatedFields is set, which is for looking up an existing reference.
 */
function getAncestorFieldDescriptors(
  ancestorVariables: AncestorNodeContext[],
  typeFilter?: VariableType[],
  triggerFilterContext?: TriggerFilterContext | null,
  includeGatedFields = false
): AncestorFieldDescriptor[] {
  const descriptors: AncestorFieldDescriptor[] = [];

  for (const nodeVars of ancestorVariables) {
    const groupName = nodeVars.nodeDisplayName;
    const getGroupIcon = nodeVars.nodeInfo.getIcon;
    const category = nodeVars.nodeInfo.category;

    // A field is hidden when the trigger's settings make it irrelevant.
    const isHiddenByTrigger = (fieldName: string): boolean =>
      !!triggerFilterContext &&
      checkFieldVisibility(fieldName, nodeVars.nodeId, triggerFilterContext);

    // The meta-variable a field belongs to, tagged so the grouped picker can
    // give each meta-variable its own group per publishing node.
    const metaTag = (variable: VariableDesc) => ({
      meta: {
        baseName: variable.name,
        displayName: getVariableDisplayName(variable),
      },
    });

    // Descriptor for a variable's own name. A meta-variable's bare ID (e.g.
    // "shipment_id") is tagged so it joins that meta-variable's group.
    const baseDescriptor = (output: NodeOutput): AncestorFieldDescriptor => {
      const variable = output.variable!;
      const isMetaVariable = getDerivedVariables(variable.type) !== undefined;
      return {
        nodeId: nodeVars.nodeId,
        groupName,
        getGroupIcon,
        category,
        variableName: variable.name,
        source: output.source,
        displayName: getVariableDisplayName(variable),
        exampleString: getVariableExample(variable),
        type: variable.type,
        isEnum: getEnumValues(variable.type) !== undefined,
        isMultiSelect: variable.is_multi_select,
        ...(isMetaVariable ? metaTag(variable) : {}),
      };
    };

    // Descriptor for one derived field of a meta-variable.
    const derivedDescriptor = (
      output: NodeOutput,
      d: DerivedVariableDesc
    ): AncestorFieldDescriptor => {
      const variable = output.variable!;
      return {
        nodeId: nodeVars.nodeId,
        groupName,
        getGroupIcon,
        category,
        variableName: variable.name,
        derivedVariableName: d.fieldName,
        source: output.source,
        displayName: d.displayName,
        exampleString: d.exampleString,
        type: d.type,
        isEnum: d.isEnum ?? false,
        isMultiSelect: d.isMultiSelect ?? false,
        group: d.group,
        ...metaTag(variable),
      };
    };

    for (const output of nodeVars.filteredOutputVariables) {
      const variable = output.variable!;
      const derived = includeGatedFields
        ? getDerivedVariables(variable.type)
        : getPickableDerivedVariables(variable.type);

      if (typeFilter) {
        if (typeFilter.includes(variable.type)) {
          if (derived) {
            // A meta-variable's own ID is always kept, never trigger-filtered.
            descriptors.push(baseDescriptor(output));
          } else if (!isHiddenByTrigger(variable.name)) {
            descriptors.push(baseDescriptor(output));
          }
        }
        if (derived) {
          for (const d of derived) {
            if (
              typeFilter.includes(d.type) &&
              !isHiddenByTrigger(d.fieldName)
            ) {
              descriptors.push(derivedDescriptor(output, d));
            }
          }
        }
      } else if (derived) {
        for (const d of derived) {
          if (!isHiddenByTrigger(d.fieldName)) {
            descriptors.push(derivedDescriptor(output, d));
          }
        }
      } else if (!isHiddenByTrigger(variable.name)) {
        descriptors.push(baseDescriptor(output));
      }
    }
  }

  return descriptors;
}

/**
 * Returns all variables available at a point in the graph: system variables
 * plus ancestor output variables, expanded into flat picker items.
 *
 * Meta-variable types (SHIPMENT, ALERT) are replaced with their derived fields.
 * When typeFilter is provided, only variables whose type matches are included.
 * For meta-variables matched by typeFilter, the base variable itself is emitted
 * (the consumer wants the opaque ID). Without a filter, ONLY derived fields are
 * emitted instead.
 */
export function getAvailableVariables(
  ancestorVariables: AncestorNodeContext[],
  typeFilter?: VariableType[]
): VariableListItem[] {
  const items: VariableListItem[] = [];

  // System variables appear first so they're always visible at the top.
  for (const sysVar of SYSTEM_VARIABLE_CATALOG) {
    if (typeFilter && !typeFilter.includes(sysVar.type)) {
      continue;
    }
    items.push({
      nodeId: SYSTEM_NODE_ID,
      groupName: 'System',
      variableName: sysVar.name,
      derivedVariableName: '',
      source: VariableSource.SOURCE_SYSTEM,
      displayName: sysVar.displayName,
      exampleString: sysVar.exampleString,
      type: sysVar.type,
    });
  }

  // Ancestor fields follow, each addressed to its real publishing node.
  for (const field of getAncestorFieldDescriptors(
    ancestorVariables,
    typeFilter
  )) {
    items.push({
      nodeId: field.nodeId,
      groupName: field.groupName,
      getGroupIcon: field.getGroupIcon,
      variableName: field.variableName,
      derivedVariableName: field.derivedVariableName ?? '',
      source: field.source,
      displayName: field.displayName,
      exampleString: field.exampleString,
      type: field.type,
    });
  }

  return items;
}

/**
 * Returns the fields a node can reference, grouped for the dropdown variable
 * picker: the System group first, then one group per ancestor node holding that
 * node's fields. Meta-variables are expanded into their derived fields, each
 * addressed to its real publishing node so the saved reference resolves at
 * runtime. A derived field carries its own name in derived_field_name, with the
 * meta-variable's name in field_name.
 *
 * @param ancestorVariables - Variables published by the node's ancestors.
 * @param typeFilter - When given, keeps only fields of a matching type.
 * @param triggerFilterContext - When given, drops fields the trigger makes irrelevant.
 */
export function getAvailableVariablesGroup(
  ancestorVariables: AncestorNodeContext[],
  typeFilter?: VariableType[],
  triggerFilterContext?: TriggerFilterContext | null,
  includeGatedFields = false
): FieldGroup[] {
  const groups: FieldGroup[] = [];

  // System variables are always the first group, never trigger-filtered.
  const sysItems: FieldItem[] = [];
  for (const sysVar of SYSTEM_VARIABLE_CATALOG) {
    if (typeFilter && !typeFilter.includes(sysVar.type)) continue;

    sysItems.push({
      field_name: sysVar.name,
      source: VariableSource.SOURCE_SYSTEM,
      display_name: sysVar.displayName,
      type: sysVar.type as number,
      is_multi_select: false,
      example_string: sysVar.exampleString,
    });
  }
  if (sysItems.length > 0) {
    groups.push({
      group_name: 'System',
      color: 'var(--text-secondary)',
      name: 'system_variables',
      node_id: SYSTEM_NODE_ID,
      items: sysItems,
    });
  }

  // Each meta-variable gets its own group so a node's Shipment fields sit apart
  // from its plain variables. A node's plain variables share one group under the
  // node's name. Groups keep the order their fields are first seen. Meta group
  // labels are the meta-variable's name ("Shipment"); collisions are resolved
  // below by appending the publishing node.
  const fieldGroups = new Map<string, FieldGroup>();
  // Meta groups paired with their publishing node label, so a colliding label
  // can be disambiguated as "Shipment (Status Progress)" in the pass below.
  const metaGroups: Array<{ group: FieldGroup; nodeLabel: string }> = [];
  for (const field of getAncestorFieldDescriptors(
    ancestorVariables,
    typeFilter,
    triggerFilterContext,
    includeGatedFields
  )) {
    const key = field.meta
      ? `${field.nodeId}.${field.meta.baseName}`
      : `${field.nodeId}.variables`;
    let group = fieldGroups.get(key);
    if (!group) {
      group = {
        group_name: field.meta ? field.meta.displayName : field.groupName,
        color: categoryIconColors[field.category] || 'var(--text-secondary)',
        name: key,
        node_id: field.nodeId,
        items: [],
      };
      fieldGroups.set(key, group);
      groups.push(group);
      if (field.meta) {
        metaGroups.push({ group, nodeLabel: field.groupName });
      }
    }
    group.items.push({
      field_name: field.variableName,
      derived_field_name: field.derivedVariableName,
      source: field.source,
      display_name: field.displayName ?? field.variableName,
      type: field.type as number,
      is_multi_select: field.isMultiSelect,
      is_enum: field.isEnum,
      group: field.group,
      example_string: field.exampleString,
    });
  }

  // Append the publishing node only to meta groups whose label is shared by
  // another node, so a unique "Shipment" stays clean.
  const metaLabelCounts = new Map<string, number>();
  for (const { group } of metaGroups) {
    metaLabelCounts.set(
      group.group_name,
      (metaLabelCounts.get(group.group_name) ?? 0) + 1
    );
  }
  for (const { group, nodeLabel } of metaGroups) {
    if ((metaLabelCounts.get(group.group_name) ?? 0) > 1) {
      group.group_name = `${group.group_name} (${nodeLabel})`;
    }
  }

  return groups;
}
